// pages/module-pages/support/tickets/TicketsPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, Filter, X, Eye, Edit, Trash2, User, ChevronDown, ChevronRight } from "lucide-react";
import TicketList from "../../../../components/features/support/tickets/TicketsList";
import TicketSaveModal from "../../../../components/features/support/tickets/TicketSaveModal";
import TicketDetailsModal from "../../../../components/features/support/tickets/TicketDetailsModal";
import DeleteConfirmationModal from "../../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../../components/ui/MenuModal";
import useTickets from "../../../../hooks/support/tickets/useTickets";
import { Ticket } from "../../../../types/features/support/Ticket";
import { alertSuccess, alertError } from "../../../../helpers/alertError";
import PageListLayout from "../../../../layouts/PageListLayout";

export default function TicketsPage() {
  const { tickets, updateTicket, deleteTicket, loading, pagination, fetchTickets } = useTickets();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketToView, setTicketToView] = useState<Ticket | null>(null);
  const [ticketToEdit, setTicketToEdit] = useState<Ticket | null>(null);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredTickets = useMemo(() => {
    let result = tickets;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (t) => t.title.toLowerCase().includes(searchLower) || t.client_name?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedPriority) {
      result = result.filter((t) => t.priority === selectedPriority);
    }

    if (selectedStatus) {
      result = result.filter((t) => t.status === selectedStatus);
    }

    return result;
  }, [tickets, searchTerm, selectedPriority, selectedStatus]);

  // Grouper les tickets par client et trier par date du dernier ticket
  const groupedTickets = useMemo(() => {
    const groups: { [clientId: string]: { clientName: string; tickets: Ticket[]; lastTicketDate: Date } } = {};

    filteredTickets.forEach((ticket) => {
      const clientId = ticket.client_id || "no-client";
      const clientName = ticket.client_name || "Client inconnu";
      const ticketDate = new Date(ticket.created_at);

      if (!groups[clientId]) {
        groups[clientId] = { clientName, tickets: [], lastTicketDate: ticketDate };
      }
      groups[clientId].tickets.push(ticket);

      // Mettre à jour la date du dernier ticket si ce ticket est plus récent
      if (ticketDate > groups[clientId].lastTicketDate) {
        groups[clientId].lastTicketDate = ticketDate;
      }
    });

    return groups;
  }, [filteredTickets]);

  // Trier les clients par date du dernier ticket (le plus récent en premier)
  const sortedClientIds = useMemo(() => {
    return Object.keys(groupedTickets).sort((a, b) => {
      const dateA = groupedTickets[a].lastTicketDate;
      const dateB = groupedTickets[b].lastTicketDate;
      return dateB.getTime() - dateA.getTime(); // Plus récent en premier
    });
  }, [groupedTickets]);

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const toggleAllClients = () => {
    const clientIds = Object.keys(groupedTickets);
    if (expandedClients.size === clientIds.length) {
      setExpandedClients(new Set());
    } else {
      setExpandedClients(new Set(clientIds));
    }
  };

  const stats = {
    total: tickets.length,
    nouveau: tickets.filter((t) => t.status === "nouveau").length,
    en_cours: tickets.filter((t) => t.status === "en_cours").length,
    escalade: tickets.filter((t) => t.status === "escalade").length,
    resolu: tickets.filter((t) => t.status === "resolu").length,
  };

  const handleStatusChange = async (id: string, status: Ticket["status"]) => {
    const success = await updateTicket(id, { status });
    if (success) {
      alertSuccess(
        `Ticket ${status === "resolu" ? "résolu" : status === "en_cours" ? "pris en charge" : "mis à jour"}`
      );
      setDetailsModalOpen(false);
      setTicketToView(null);
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async () => {
    if (!ticketToDelete) return;
    setIsDeleting(true);
    const success = await deleteTicket(ticketToDelete.id);
    setIsDeleting(false);
    if (success) {
      setTicketToDelete(null);
      alertSuccess("Ticket supprimé avec succès");
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    let success = true;
    for (const id of itemsToDelete) {
      const result = await deleteTicket(id);
      if (!result) success = false;
    }
    if (success) {
      setItemsToDelete(null);
      setSelectedIds([]);
      alertSuccess(`${itemsToDelete.length} ticket(s) supprimé(s)`);
      fetchTickets(currentPage, pagination.perPage);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedPriority("");
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedPriority !== "" || selectedStatus !== "" || searchTerm.trim() !== "";

  const getActionsMenu = (ticket: Ticket): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          setTicketToView(ticket);
          setDetailsModalOpen(true);
          setSelectedTicket(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          setTicketToEdit(ticket);
          setModalOpen(true);
          setSelectedTicket(null);
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          setTicketToDelete(ticket);
          setSelectedTicket(null);
        },
      },
    ];
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchTickets(page, pagination.perPage, {
      status: selectedStatus || undefined,
      priority: selectedPriority || undefined,
    });
  };

  // Rendu d'un groupe de tickets par client
  const renderClientGroup = (clientId: string, clientName: string, clientTickets: Ticket[]) => {
    const isExpanded = expandedClients.has(clientId);
    const clientUnread = clientTickets.filter((t) => t.status === "nouveau" || t.status === "en_cours").length;

    const lastTicket =
      clientTickets.length > 0
        ? clientTickets.reduce((latest, current) => {
            const latestDate = new Date(latest.created_at);
            const currentDate = new Date(current.created_at);
            return currentDate > latestDate ? current : latest;
          })
        : null;

    const lastTicketDate = lastTicket ? new Date(lastTicket.created_at) : null;

    return (
      <div key={clientId} className="border border-gray-200 rounded-xl overflow-hidden mb-3">
        {/* En-tête du client */}
        <div
          onClick={() => toggleClient(clientId)}
          className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <span className="font-medium text-gray-900">{clientName}</span>
              <span className="text-sm text-gray-500 ml-2">
                ({clientTickets.length} ticket{clientTickets.length > 1 ? "s" : ""})
              </span>
              {clientUnread > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full">
                  {clientUnread} en attente
                </span>
              )}
              {lastTicket && <p className="text-xs text-gray-400 mt-0.5">Dernier ticket : {lastTicket.title}</p>}
            </div>
          </div>
          {lastTicketDate && (
            <span className="text-xs text-gray-400">
              {lastTicketDate.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>

        {/* Tickets du client */}
        {isExpanded && (
          <div className="p-2">
            <TicketList
              tickets={clientTickets}
              onAction={(ticket) => setSelectedTicket(ticket)}
              onSelectTickets={(selected) => setSelectedIds(selected.map((t) => t.id))}
              selectable={true}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: () => setItemsToDelete(selectedIds),
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  };

  const clientIds = Object.keys(groupedTickets);
  const allExpanded = expandedClients.size === clientIds.length && clientIds.length > 0;

  return (
    <PageListLayout title="Gestion des tickets">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les réclamations et demandes clients</p>
            </div>
            <div className="flex gap-2">
              {clientIds.length > 0 && (
                <button
                  onClick={toggleAllClients}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
                >
                  {allExpanded ? "Réduire tout" : "Développer tout"}
                </button>
              )}
              <button
                onClick={() => {
                  setTicketToEdit(null);
                  setModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
              >
                <Plus size={18} />
                Nouveau ticket
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-700/70">Total</p>
              <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Nouveaux</p>
              <p className="text-2xl font-bold text-blue-700">{stats.nouveau}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
              <p className="text-xs text-purple-700/70">En cours</p>
              <p className="text-2xl font-bold text-purple-700">{stats.en_cours}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
              <p className="text-xs text-red-700/70">Escalade</p>
              <p className="text-2xl font-bold text-red-700">{stats.escalade}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Résolus</p>
              <p className="text-2xl font-bold text-green-700">{stats.resolu}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un ticket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <select
                      value={selectedPriority}
                      onChange={(e) => setSelectedPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Toutes</option>
                      <option value="basse">Basse</option>
                      <option value="moyenne">Moyenne</option>
                      <option value="haute">Haute</option>
                      <option value="critique">Critique</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Tous</option>
                      <option value="nouveau">Nouveau</option>
                      <option value="en_cours">En cours</option>
                      <option value="en_attente">En attente</option>
                      <option value="escalade">Escalade</option>
                      <option value="resolu">Résolu</option>
                      <option value="ferme">Fermé</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : Object.keys(groupedTickets).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun ticket trouvé</p>
          </div>
        ) : (
          <>
            {/* Tickets groupés par client - triés par date du dernier ticket */}
            <div className="space-y-3">
              {sortedClientIds.map((clientId) => {
                const { clientName, tickets: clientTickets } = groupedTickets[clientId];
                return renderClientGroup(clientId, clientName, clientTickets);
              })}
            </div>

            {pagination.total > 0 && (
              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {pagination.lastPage} ({pagination.total} tickets)
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.lastPage}
                  className="px-4 py-2 text-gray-600 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Menu modal des actions d'un ticket */}
      {selectedTicket && (
        <MenuModal
          title={selectedTicket.title}
          isOpen={true}
          onClose={() => setSelectedTicket(null)}
          icon={null}
          menu={getActionsMenu(selectedTicket)}
        />
      )}

      {/* Modal de création/édition */}
      <TicketSaveModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setTicketToEdit(null);
        }}
        onSuccess={() => {
          setModalOpen(false);
          alertSuccess(ticketToEdit ? "Ticket modifié" : "Ticket créé");
          setTicketToEdit(null);
          fetchTickets(currentPage, pagination.perPage);
        }}
        initialData={ticketToEdit || undefined}
      />

      {/* Modal de détails */}
      <TicketDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setTicketToView(null);
        }}
        ticket={ticketToView}
        onStatusChange={handleStatusChange}
      />

      {/* Modal suppression individuelle */}
      <DeleteConfirmationModal
        isOpen={!!ticketToDelete}
        onClose={() => setTicketToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le ticket"
        message={`Supprimer le ticket "${ticketToDelete?.title}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      {/* Modal suppression multiple groupée */}
      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} ticket(s) ?`}
      />
    </PageListLayout>
  );
}
